import {
  Modal,
  Tag,
  Button,
  Typography,
  Divider,
  Space,
  Row,
  Col,
  Collapse,
  Checkbox,
} from 'antd';
import { useState, useRef, useMemo, useEffect } from 'react';
import type { ModalProps } from 'antd';
import QRCode from 'react-qr-code';
import dayjs from 'dayjs';
import moment from 'moment';

import { useListStickerWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import './style.less';
import {
  ListStickerWorkOrdersResponse,
  ServicesListType,
  WorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import TableServicesPreviouslyPerformed from './TableServicesPreviouslyPerformed';
import numberFormatter from '@/utils/number/numberFormatter';
import GenerateImageButton from './GenerateImageButton';
import PrintButton from './PrintButton';
import EtiquetteCard from '@/components/_common/EtiquetteCard';
import EtiquetteHeader from '@/components/_common/EtiquetteHeader';
import { FloatingFormItem } from '@/components/_common';
import EtiquetteWhiteCard from './EtiquetteWhiteCard';

interface Props extends ModalProps {
  workOrder?: WorkOrder;
  complement?: WorkOrderComplement;
}

export enum WorkOrderEtiquetteFields {
  header = 'header',
  complementIdentifier = 'complementIdentifier',
  serviceName = 'serviceName',
  qrCode = 'qrCode',
  exchangeKm = 'exchangeKm',
  kmNextChange = 'kmNextChange',
  carriedOut = 'carriedOut',
  return = 'return',
  currentDate = 'currentDate',
  observation = 'observation',
}

export default function WorkOrderReviewEtiquetteModal({
  workOrder,
  complement,
  ...props
}: Props) {
  const { Text } = Typography;
  const previewRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const AVAILABLE_FIELDS = [
    { label: 'Cabeçalho', value: WorkOrderEtiquetteFields.header },
    { label: 'OS', value: WorkOrderEtiquetteFields.complementIdentifier },
    { label: 'Serviço', value: WorkOrderEtiquetteFields.serviceName },
    { label: 'QRCode', value: WorkOrderEtiquetteFields.qrCode },
    { label: 'KM da troca', value: WorkOrderEtiquetteFields.exchangeKm },
    { label: 'KM próxima troca', value: WorkOrderEtiquetteFields.kmNextChange },
    { label: 'Realizado', value: WorkOrderEtiquetteFields.carriedOut },
    { label: 'Retorno', value: WorkOrderEtiquetteFields.return },
    { label: 'Observação', value: WorkOrderEtiquetteFields.observation },
    { label: 'Data atual', value: WorkOrderEtiquetteFields.currentDate },
  ];

  const EMPTY = '_________________________';
  const STORAGE_KEY = 'workOrderReviewEtiquetteFields';
  const DEFAULT_FIELDS = Object.values(WorkOrderEtiquetteFields);

  const renderFormatKm = (value?: number | string) =>
    value ? numberFormatter(value as number) : EMPTY;

  const renderFormatDate = (value?: string) =>
    value ? moment(value).format('DD/MM/YYYY') : EMPTY;

  function handleResolveServiceFields(
    item: ServicesListType,
    complement?: WorkOrderComplement,
  ) {
    const isWhite = item.id === 0;
    return {
      exitKilometers: isWhite
        ? item.exitKilometers
        : renderFormatKm(complement?.exitKilometers),
      nextRevisionInKilometers: isWhite
        ? item.nextRevisionInKilometers
        : renderFormatKm(item.nextRevisionInKilometers),
      returnDate: isWhite
        ? item.returnDate
        : renderFormatDate(complement?.returnDate),
      nextRevisionDate: isWhite
        ? item.nextRevisionDate
        : renderFormatDate(item.nextRevisionDate),
    };
  }

  const [fields, setFields] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_FIELDS;
    } catch {
      return DEFAULT_FIELDS;
    }
  });

  const [selectedIndex, setSelectedIndex] = useState<number[]>([]);
  const [showLastServices, setShowLastServices] = useState(true);
  const [itemServices, setItemServices] = useState<{
    firstItem: ListStickerWorkOrdersResponse;
    restItems?: ListStickerWorkOrdersResponse[];
  }>();

  const has = (field: WorkOrderEtiquetteFields) => fields.includes(field);
  const showQrCode = has(WorkOrderEtiquetteFields.qrCode);

  const infosRequest = useMemo(
    () => ({
      complementId: complement?.id,
      licensePlate: workOrder?.vehicle?.licensePlate,
      companyId: workOrder?.company?.id,
    }),
    [complement?.id, workOrder?.vehicle?.licensePlate, workOrder?.company?.id],
  );

  const listStickerWorkOrders = useListStickerWorkOrders(infosRequest);
  const workOrderAuditComplementsData = flatInfiniteData(
    listStickerWorkOrders.data,
  );

  useEffect(() => {
    if (!workOrderAuditComplementsData) return;
    const [firstItem, ...restItems] = workOrderAuditComplementsData;
    setItemServices({ firstItem, restItems });
  }, [listStickerWorkOrders.dataUpdatedAt]);

  const toggleField = (value: string) => {
    setFields((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((f) => f !== value)
        : [...prev, value];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const toggleSelect = (index: number) => {
    setSelectedIndex((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const whiteCardIndex =
    (itemServices?.firstItem?.servicesList?.length ?? 0) + 1;

  const sharedButtonProps = {
    showQrCode,
    selectedIndex,
    previewRefs,
    infosRequest: { ...infosRequest, showLastServices },
    complementIdentifier: complement?.complementIdentifier,
  };

  return (
    <Modal
      {...props}
      width={1020}
      title="Etiqueta de revisão"
      destroyOnClose
      footer={[
        <Button onClick={props.onCancel}>Cancelar</Button>,
        <GenerateImageButton {...sharedButtonProps} />,
        <PrintButton {...sharedButtonProps} />,
      ]}
    >
      <Text type="secondary">
        Selecione os dados que deverão aparecer na etiqueta
      </Text>

      <Space wrap style={{ margin: '12px 0' }}>
        {AVAILABLE_FIELDS.map((item) => (
          <Tag
            key={item.value}
            color={
              has(item.value as WorkOrderEtiquetteFields) ? 'blue' : undefined
            }
            onClick={() => toggleField(item.value)}
            style={{ cursor: 'pointer' }}
          >
            {item.label}
          </Tag>
        ))}
      </Space>

      <Divider plain>Pré-visualização</Divider>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text type="secondary">
            Clique nas etiquetas para selecionar quais deseja imprimir
          </Text>
        </Col>

        {itemServices?.firstItem?.servicesList?.map((item, index) => {
          const {
            exitKilometers,
            nextRevisionInKilometers,
            returnDate,
            nextRevisionDate,
          } = handleResolveServiceFields(item, complement);

          return (
            <Col span={24} md={12} key={index}>
              <EtiquetteCard
                ref={(element) => (previewRefs.current[index] = element)}
                selected={selectedIndex.includes(index)}
                onClick={() => toggleSelect(index)}
                header={
                  has(WorkOrderEtiquetteFields.header) && (
                    <EtiquetteHeader
                      companyName={workOrder?.company?.nickName}
                      complementIdentifier={complement?.complementIdentifier}
                      showOS={has(
                        WorkOrderEtiquetteFields.complementIdentifier,
                      )}
                    />
                  )
                }
              >
                <div className="etiquette-review-content">
                  <div className="etiquette-review-info">
                    {has(WorkOrderEtiquetteFields.serviceName) && (
                      <div className="etiquette-review-field">
                        <span className="etiquette-review-field-value">
                          Serviço:
                        </span>{' '}
                        {item.serviceName}
                      </div>
                    )}

                    {has(WorkOrderEtiquetteFields.exchangeKm) && (
                      <div className="etiquette-review-field">
                        <span className="etiquette-review-field-value">
                          KM da troca:
                        </span>{' '}
                        {exitKilometers}
                      </div>
                    )}

                    {has(WorkOrderEtiquetteFields.kmNextChange) && (
                      <div className="etiquette-review-field">
                        <span className="etiquette-review-field-value">
                          KM próx. troca:
                        </span>{' '}
                        {nextRevisionInKilometers}
                      </div>
                    )}

                    {has(WorkOrderEtiquetteFields.carriedOut) && (
                      <div className="etiquette-review-field">
                        <span className="etiquette-review-field-value">
                          Realizado:
                        </span>{' '}
                        {returnDate}
                      </div>
                    )}

                    {has(WorkOrderEtiquetteFields.return) && (
                      <div className="etiquette-review-field">
                        <span className="etiquette-review-field-value">
                          Retorno:
                        </span>{' '}
                        {nextRevisionDate}
                      </div>
                    )}
                  </div>

                  {showQrCode && (
                    <div className="etiquette-review-qrcode">
                      <div className="etiquette-review-qrcode-preview-wrapper">
                        <QRCode value="" size={90} />
                        <div className="etiquette-review-qrcode-preview-overlay">
                          <span>Preview</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {(has(WorkOrderEtiquetteFields.observation) ||
                  has(WorkOrderEtiquetteFields.currentDate)) && (
                  <div className="etiquette-review-footer">
                    {has(WorkOrderEtiquetteFields.observation) && (
                      <span>Obs:</span>
                    )}
                    {has(WorkOrderEtiquetteFields.currentDate) && (
                      <span>{dayjs().format('DD/MM/YYYY')}</span>
                    )}
                  </div>
                )}
              </EtiquetteCard>
            </Col>
          );
        })}

        <Col span={24} md={12} key={whiteCardIndex}>
          <EtiquetteWhiteCard
            index={whiteCardIndex}
            fields={fields}
            showQrCode={showQrCode}
            previewRefs={previewRefs}
            setItemServices={setItemServices}
            workOrderNickName={workOrder?.company.nickName}
            complementIdentifier={complement?.complementIdentifier}
          />
        </Col>

        {showQrCode && (
          <Col span={24}>
            <FloatingFormItem initialValue={false} valuePropName="checked">
              <Checkbox
                style={{ marginTop: '12px' }}
                checked={showLastServices}
                onChange={({ target }) => setShowLastServices(target.checked)}
              >
                Exibir serviços anteriores no QRCode
              </Checkbox>
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24}>
          <Collapse>
            <Collapse.Panel header="Serviços anteriores" key="previousServices">
              <TableServicesPreviouslyPerformed
                restItems={itemServices?.restItems}
                loading={listStickerWorkOrders.isLoading}
                isFetching={listStickerWorkOrders.isFetching}
                hasNextPage={listStickerWorkOrders.hasNextPage}
                fetchNextPage={listStickerWorkOrders.fetchNextPage}
              />
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Modal>
  );
}
